import { useContext, useMemo } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { VirtualCard } from 'app/components/pages/Extranet/ExtranetBookings/common/VirtualCard/VirtualCard';
import type {
  VirtualCardConfig,
  VirtualCardData,
} from 'app/components/pages/Extranet/ExtranetBookings/common/VirtualCard/types';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { useCardInfos } from 'app/hooks/data/core/extranet/bookings/useCardInfos';
import { VCC_STATUS } from 'app/typings/core/common/enum/vccStatus';
import type { BookingDetails } from 'app/typings/core/extranet/bookings/booking';
import type { VccStatus } from 'app/typings/vcc';
import { formatDayOfMonth } from 'app/utils/dates';

import { getTooltipText } from '../../../../utils';

import { getVCCStatus } from './utils/virtualCard.utils';

type VirtualCardProps = {
  bookingDetails: BookingDetails;
};

export const ModernVirtualCard = ({ bookingDetails }: VirtualCardProps) => {
  const { hotelId } = useContext(HotelContext);
  const { currencyFormatter } = useContext(CurrencyContext);

  const status = getVCCStatus(bookingDetails, currencyFormatter);
  const cardIsCancelled = status.status === VCC_STATUS.CANCELED;
  const cardIsDeactivated = status.status === VCC_STATUS.DEACTIVATED;
  const cardIsBlur = cardIsCancelled || cardIsDeactivated;

  const { getEphemeralKey } = useCardInfos(hotelId, bookingDetails.code);

  const data: VirtualCardData = useMemo(
    () => ({
      issuingCardId: bookingDetails.issuingCardId,
      issuingBalance: bookingDetails.issuingBalance,
      checkin: bookingDetails.checkIn,
      code: bookingDetails.code,
    }),
    [
      bookingDetails.issuingCardId,
      bookingDetails.issuingBalance,
      bookingDetails.checkIn,
      bookingDetails.code,
    ]
  );

  const config: VirtualCardConfig = useMemo(
    () => ({
      currencyFormatter,
      cardIsBlur,
      status,
    }),
    [currencyFormatter, cardIsBlur, status]
  );

  const stripeConfig = useMemo(
    () => ({
      getEphemeralKey,
    }),
    [getEphemeralKey]
  );

  const getTooltipTextWrapper = (statusStr: VCC_STATUS | VccStatus) => {
    const result = getTooltipText(statusStr);

    return result ?? null;
  };

  const alertConfig = useMemo(
    () => ({
      formatDate: formatDayOfMonth,
      checkinLabel: 'check-in day',
      importantMessagePart1:
        'Important: the pre-authorisation, deposit, extras or tourist tax',
      importantMessagePart2:
        "must not be made with this virtual card but with the guest's physical card.",
    }),
    []
  );

  return (
    <VirtualCard
      data={data}
      config={config}
      stripeConfig={stripeConfig}
      alertConfig={alertConfig}
      getTooltipText={getTooltipTextWrapper}
    />
  );
};
